import React, {useState, useRef, useEffect} from 'react'
import './ValidationSreens.scss'
import ValidationOutcomeText from './ValidationOutcomeText'
import {KTSVG} from '../../../_metronic/helpers'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {apiShowValidationPlaylist, apiValidatePvinNumber} from '../../redux/api'
import {useSearchParams} from 'react-router-dom'

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: TurnstileOptions) => string
      reset: (widgetId?: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface TurnstileOptions {
  sitekey: any
  callback?: (token: any) => void
  'expired-callback'?: () => void
  'error-callback'?: () => void
}

const ValidationScreens = () => {
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY
  const [screen, setScreen] = useState<any>('input')
  const [turnstileToken, setTurnstileToken] = useState<any>('')
  const [pvin, setPvin] = useState<any>('')
  const [isValidating, setIsValidating] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [response, setResponse] = useState<any>(null)
  const [turnstileReady, setTurnstileReady] = useState<boolean>(false)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [searchParams] = useSearchParams()

  const handleQueryParams = () => {
    const vehicleId = searchParams.get('vehicleId')
    const integratorId = searchParams.get('integratorId')

    if (vehicleId) {
      setPvin(vehicleId)
      setScreen('approved')
      return
    }

    if (integratorId) {
      setScreen('integrator')
      return
    }

    setScreen('input')
  }

  useEffect(() => {
    handleQueryParams()
  }, [searchParams])

  useEffect(() => {
    const SCRIPT_ID = 'cloudflare-turnstile-script'

    if (document.getElementById(SCRIPT_ID)) {
      setTurnstileReady(true)
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => setTurnstileReady(true)

    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
      if (window.turnstile) window.turnstile.reset()
    }
  }, [])

  useEffect(() => {
    if (!turnstileReady || !window.turnstile || !turnstileRef.current) {
      return
    }

    turnstileRef.current.innerHTML = ''
    setTurnstileToken('')

    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: any) => {
        setTurnstileToken(token)
      },
      'expired-callback': () => setTurnstileToken(''),
      'error-callback': () => setTurnstileToken(''),
    })
  }, [turnstileReady, screen])

  const formik = useFormik({
    initialValues: {
      pvin: '',
      turnstileToken: '',
    },
    validationSchema: Yup.object({
      pvin: Yup.string().required('PVIN is required'),
    }),
    onSubmit: async ({pvin}) => {
      if (!turnstileToken) return
      setPvin('')
      setIsValidating(true)
      validatePvinNumber()
    },
  })

  const isValidateDisabled = !formik.values.pvin || !turnstileToken || isValidating

  const resetToInput = () => {
    formik.resetForm()
    setTurnstileToken('')
    setPvin('')
    setIsValidating(false)
    setIsLoading(false)
    setResponse(null)
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
    }
    handleQueryParams()
  }

  const buildRequest = () => {
    const data = {...formik.values}
    return {
      VehicleId: data.pvin,
      ClientToken: turnstileToken,
    }
  }

  const validatePvinNumber = () => {
    if (formik.isValid && turnstileToken) {
      setPvin(formik.values.pvin)
      const requestBody = buildRequest()
      apiValidatePvinNumber(requestBody)
        .then(({data}) => {
          if (data.Status === 200 && data.Response.Valid === true) {
            if (window.turnstile && widgetIdRef.current) {
              window.turnstile.reset(widgetIdRef.current)
            }
            setTurnstileToken('')
            setScreen('approved')
          } else if (data.Status === 200 && data.Response.Valid === false) {
            if (window.turnstile && widgetIdRef.current) {
              window.turnstile.reset(widgetIdRef.current)
            }
            setTurnstileToken('')
            setScreen('failed')
          }
          setIsValidating(false)
        })
        .catch((res: any) => {
          resetToInput()
        })
    }
  }
  const showValidatePlaylist = () => {
    if (!pvin && !turnstileToken) {
      return
    }
    const requestBody = {
      VehicleId: pvin,
      ClientToken: turnstileToken,
    }
    setIsLoading(true)
    setResponse(null)
    apiShowValidationPlaylist(requestBody)
      .then(({data}) => {
        if (data.Status === 200) {
          setResponse(data)
        } else {
          setResponse(data)
        }
        setIsLoading(false)
      })
      .catch((res: any) => {
        setIsLoading(false)
        resetToInput()
      })
  }

  return (
    <div className='container-fluid min-vh-100 d-flex align-items-center justify-content-center validation-screens'>
      <div className='card shadow-lg validation-screens-card'>
        <div className='card-body d-flex flex-column main-container'>
          <div
            className='logo-container border-bottom'
            style={{cursor: 'pointer'}}
            onClick={() => {
              resetToInput()
            }}
            title='Back to start'
          >
            <h3 className='text-danger fw-bold m-0'>DFHV</h3>
          </div>

          {screen === 'approved' && (
            <div
              className='flex-grow-1 d-flex flex-column justify-content-center'
              style={{maxWidth: '390px', width: '100%'}}
            >
              <div className='d-flex flex-column justify-content-center input-screen-container'>
                <span className='d-flex align-items-center justify-content-center flex-column approved-title-container'>
                  <KTSVG
                    path='/media/icons/duotone/General/verify-icon.svg'
                    className='text-success svg-icon-2hx'
                  />
                  <h1 className='approved-title text-center'>Vehicle is approved!</h1>
                </span>
                <div className='approved-pvin-container'>
                  <span className='approved-pvin-text'>{pvin}</span>
                </div>
                <div className='d-flex align-items-center flex-column justify-content-center approved-description-container'>
                  <p className='approved-description-1 text-center'>
                    This taxi is approved by DFHV.
                  </p>
                  <p className='approved-description-2 text-center'>Enjoy your ride!</p>
                </div>
                <span className='d-flex align-items-center justify-content-center'>
                  <div ref={turnstileRef} className='turnstile-container' />
                </span>
                <button
                  type='submit'
                  className='btn show-message-button w-100'
                  disabled={!pvin || !turnstileToken || isLoading}
                  onClick={() => {
                    showValidatePlaylist()
                  }}
                >
                  {isLoading && (
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                  )}
                  {isLoading ? 'Processing...' : 'Show message on rooftop display'}
                </button>
                {response && <ValidationOutcomeText response={response} />}
              </div>
            </div>
          )}

          {screen === 'failed' && (
            <div className='flex-grow-1 d-flex flex-column justify-content-center'>
              <div className='d-flex flex-column justify-content-center input-screen-container'>
                <span className='d-flex align-items-center justify-content-center flex-column failed-title-container'>
                  <KTSVG
                    path='/media/icons/duotone/General/error-icon.svg'
                    className='text-danger svg-icon-2hx'
                  />
                  <h1 className='failed-title text-center'>Validation failed</h1>
                </span>
                <div className='failed-pvin-container'>
                  <span className='failed-pvin-text'>{pvin}</span>
                </div>
                <div className='d-flex align-items-center flex-column justify-content-center failed-description-container'>
                  <span className='failed-description-1 text-center'>
                    <p>This PVIN could not be validated.</p>
                    <p>Please try again.</p>
                  </span>
                  <p className='failed-description-2 text-center'>
                    Make sure you enter the number correctly.
                  </p>
                </div>
                <form
                  onSubmit={formik.handleSubmit}
                  className='d-flex flex-column align-items-center justify-content-center form-container'
                >
                  <input
                    placeholder='Enter PVIN'
                    {...formik.getFieldProps('pvin')}
                    className='form-control form-control-lg form-control-solid vehicle-pvin-input-field'
                  />

                  <div ref={turnstileRef} className='turnstile-container' />

                  <button
                    type='submit'
                    className='btn btn-dark vehicle-pvin-validate-button w-100'
                    disabled={isValidateDisabled}
                  >
                    {isValidating ? 'Validating...' : 'Validate'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {screen === 'input' && (
            <div className='flex-grow-1 d-flex flex-column justify-content-center'>
              <div className='d-flex flex-column justify-content-center input-screen-container'>
                <span className='d-flex flex-column justify-content-center vehicle-pvin-container'>
                  <h1 className='text-center vehicle-pvin-title'>
                    <span>Enter vehicle PVIN number</span>
                    <br />
                    <span>to validate this taxi</span>
                  </h1>
                  <p className='text-center vehicle-pvin-description'>
                    The PVIN number is visible on the taxi.
                  </p>
                </span>
                <form
                  onSubmit={formik.handleSubmit}
                  className='d-flex flex-column align-items-center justify-content-center form-container'
                >
                  <input
                    placeholder='Enter PVIN'
                    {...formik.getFieldProps('pvin')}
                    className='form-control form-control-lg form-control-solid vehicle-pvin-input-field'
                  />
                  <div ref={turnstileRef} className='turnstile-container' />

                  <button
                    type='submit'
                    className='btn btn-dark vehicle-pvin-validate-button w-100'
                    disabled={isValidateDisabled}
                  >
                    {isValidating ? 'Validating...' : 'Validate'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {screen === 'integrator' && (
            <div className='flex-grow-1 d-flex flex-column justify-content-center'>
              <div className='text-center'>
                <h2>Integrator id is not supported yet.</h2>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ValidationScreens
